/* globals window alert */
import React, { useState, useEffect, useContext } from 'react';
import {
  useUpdate,
  useCreate,
  useNotify,
  useRefresh,
  TextInput,
  SelectArrayInput,
  SaveButton,
  FormWithRedirect,
  required,
} from 'react-admin';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import HistoryIcon from '@material-ui/icons/History';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { Checkbox, Tooltip } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { CalendarEditAvailabilityContext } from '../../Context';
import { updateReload } from '../../actions';
import { convertTime, getZ3PConfigValue } from '../../helper';
import { getScheduleStats } from './scheduleApiHelpers';
import { apiPut } from '../../brainApi';
import {
  convertToMinutes,
  createTimeBlocksFromExistingArray,
  generateTimes,
  recalculateDurations,
} from './availibilityFieldHelpers';
import AvailabilityFields from './AvailabilityFields';

const useStyles = makeStyles(() => ({
  listLabel: {
    color: '#888',
    marginRight: '15px',
  },
  infoList: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
      marginRight: 5,
    },
    '& .MuiFormHelperText-marginDense': {
      display: 'none',
    },
  },
  compactInfoList: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-evenly',
  },
  listInput: {
    marginBottom: 20,
  },
  hideHelperText: {
    '& .MuiFormHelperText-marginDense': {
      display: 'none',
    },
  },
  timeInput: {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-adornedEnd': {
      padding: '0',
    },
    '& .MuiIconButton-root': {
      display: 'none',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 6px',
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      display: 'none',
      '-webkit-appearance': 'none',
    },
  },
  durationInput: {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiSelect-iconOutlined': {
      right: 0,
    },
  },
  timeSeparator: {
    margin: '0 7px 0 12px',
    color: '#888',
  },
  width50: {
    width: '30%',
  },
  actionButtons: {
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
  },
  vehicleLabel: {
    '& .MuiFormControlLabel-label': {
      fontWeight: '700',
    },
  },
  dispatchLink: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  selectedDateText: {
    color: '#296690',
    fontWeight: 'bold',
    padding: '10px 0',
  },
  editButton: {
    color: '#296690',
    textDecoration: 'underline',
    paddingLeft: '5px',
    cursor: 'pointer',
  },
  expandArrow: {
    cursor: 'pointer',
    float: 'right',
    marginLeft: '5px',
    position: 'relative',
    top: '6px',
  },
  expandSection: {
    minHeight: '160px',
  },
  resetButton: {
    marginTop: '5px',
  },
}));

const getTime = (date) => {
  const time = date.split(' ')[1];
  const hourMin = time.split(':');
  return `${hourMin[0]}:${hourMin[1]}`;
};

export const getLengthOfDay = (startTime, endTime) => {
  let start = moment(startTime, 'YYYY-MM-DD HH:mm:ss');
  if (!start._isValid) {
    start = moment(startTime, 'HH:mm:ss');
  }
  let end = moment(endTime, 'YYYY-MM-DD HH:mm:ss');
  if (!end._isValid) {
    end = moment(endTime, 'HH:mm:ss');
  }

  const diff = end.diff(start);
  const duration = moment.duration(diff);
  return duration.asHours();
};

const SingleDayAvailabilityForm = ({
  formData,
  default_schedule_types = null,
  isServiceBay,
}) => {
  const {
    selectedDate,
    selectedVehicle,
    setRefreshExceptionTable,
    allScheduleTypes,
    setScheduleData,
    start,
    end,
    scheduleExceptions,
    recurringDayDetails,
    singleDayBooleanHasChanged,
    setSingleDayBooleanHasChanged,
    hasSingleDayTimeBlockError,
    setHasSingleDayTimeBlockError,
  } = useContext(CalendarEditAvailabilityContext);
  const defaultDuration = 1;
  const { id: serviceVehicleId } = selectedVehicle;
  const { id, offered_service_types, start_time = '', end_time = '' } =
    formData || {};
  const [allowFutureBookings, setAllowFutureBookings] = useState(true);
  const [isCanceled, setIsCanceled] = useState(false);
  const [serviceTypes, setServiceTypes] = useState(
    offered_service_types?.length > 0
      ? offered_service_types
      : default_schedule_types,
  );
  const [serviceTypesIsSet, setServiceTypesIsSet] = useState(!!serviceTypes);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [canResetSchedule, setCanResetSchedule] = useState(false);
  const [hideTimeInputs, setHideTimeInputs] = useState(true);
  const [fieldError, setFieldError] = useState(false);
  const [startTimeError, setStartTimeError] = useState(false);
  const [endTimeError, setEndTimeError] = useState(false);
  const [singleDayTimeBlocks, setSingleDayTimeBlocks] = useState([]);
  const [startTimeValue, setStartTimeValue] = useState(null);
  const [endTimeValue, setEndTimeValue] = useState(null);

  const classes = useStyles();
  const [update] = useUpdate('schedule');
  const [create] = useCreate('schedule');
  const dispatch = useDispatch();
  const notify = useNotify();
  const refresh = useRefresh();

  const { is_using_time_blocks: isUsingTimeBlocks } = getZ3PConfigValue();

  const clearErrors = () => {
    setStartTimeError(false);
    setEndTimeError(false);
    setFieldError(false);
  };

  useEffect(() => {
    const defaultStartTime =
      convertTime(getZ3PConfigValue('time_range_min'), 'militaryString') ||
      '09:00';
    const defaultEndTime =
      convertTime(getZ3PConfigValue('time_range_max'), 'militaryString') ||
      '17:00';

    const startTime = start_time ? getTime(start_time) : defaultStartTime;
    const endTime = end_time ? getTime(end_time) : defaultEndTime;

    setStartTimeValue(startTime);
    setEndTimeValue(endTime);

    if (isUsingTimeBlocks) {
      const predeterminedSlotTimes = formData?.predetermined_slot_times || [];

      const slotTimes =
        predeterminedSlotTimes.length > 0
          ? createTimeBlocksFromExistingArray(
              predeterminedSlotTimes,
              startTime,
              endTime,
            )
          : generateTimes(startTime, endTime, defaultDuration);

      setSingleDayTimeBlocks(slotTimes);
    }
  }, []);

  const getCanceledException = () => {
    return scheduleExceptions.find(
      (exception) =>
        exception.is_canceled &&
        moment(exception.start_time).format('YYYY-MM-DD') ===
          moment(selectedDate).format('YYYY-MM-DD'),
    );
  };

  const checkForResetOption = () => {
    const scheduleInstanceHasRecurringSchedule = recurringDayDetails.find(
      (recurring) =>
        recurring.recurring_schedule_id === formData.recurring_schedule_id,
    );
    const isException = scheduleExceptions.find(
      (exception) => exception.id === id,
    );
    const isCanceledException = getCanceledException();
    const dateHasRecurringSchedule = recurringDayDetails.find(
      (recurring) =>
        !recurring.is_canceled &&
        recurring.day_of_week === moment(selectedDate).format('dddd'),
    );
    const recurringScheduleInstanceIsException =
      scheduleInstanceHasRecurringSchedule && isException;
    const canceledExceptionHasRecurring =
      isCanceledException && dateHasRecurringSchedule;
    setCanResetSchedule(
      recurringScheduleInstanceIsException || canceledExceptionHasRecurring,
    );
    //  If a day is unavailable, hide the time inputs initially
    //  Identify unavailable dates by dates that can be reset but don't have an ID
    setHideTimeInputs(
      !formData?.id &&
        canceledExceptionHasRecurring &&
        !singleDayBooleanHasChanged,
    );
  };

  useEffect(() => {
    if (formData?.id) {
      if (formData?.stats?.num_bookings > 0) {
        setAllowFutureBookings(formData?.limit_scheduled_works !== 0);
      }
      setServiceTypes(offered_service_types);
    } else {
      setAllowFutureBookings(true);
      setServiceTypes(default_schedule_types);
    }
    checkForResetOption();
  }, [formData]);

  useEffect(() => {
    setSingleDayBooleanHasChanged(false);
    if (!serviceTypesIsSet) setIsFormExpanded(true);
  }, [selectedDate]);

  const refreshAfterUpdate = () => {
    setIsSaving(false);
    notify('Single Day Availability Successfully Updated');
    refresh();
    dispatch(updateReload(true));
    setRefreshExceptionTable(true);
    checkForResetOption();
    getScheduleStats(serviceVehicleId, start, end).then(setScheduleData);
    setSingleDayBooleanHasChanged(false);
  };

  const handleResetSchedule = () => {
    let recordToUpdate = formData;
    // If resetting a cancelled instance, we need to find its ID
    if (!formData?.id) {
      const canceledSchedule = getCanceledException();
      if (canceledSchedule) {
        recordToUpdate = canceledSchedule;
      }
    }
    setIsSaving(true);
    const recurringSchedule = recurringDayDetails.find(
      (recurring) =>
        recurring.recurring_schedule_id ===
        recordToUpdate.recurring_schedule_id,
    );
    if (!recurringSchedule) {
      return alert(
        'Something went wrong. Please manually update the schedule details.',
      );
    }
    const valuesToUpdate = [
      'start_time',
      'end_time',
      'is_canceled',
      'limit_scheduled_works',
      'limit_total_hours',
      'limit_total_driving_hours',
      'offered_service_types',
      'client_location_ids',
      'predetermined_slot_times',
    ];
    valuesToUpdate.forEach((value) => {
      if (['start_time', 'end_time'].includes(value)) {
        recordToUpdate[value] = `${moment(selectedDate).format('YYYY-MM-DD')} ${
          recurringSchedule[value]
        }`;
      } else {
        recordToUpdate[value] = recurringSchedule[value];
      }
    });
    recordToUpdate.is_exception_to_recurring_schedule = false;
    apiPut(`/schedule/${recordToUpdate.id}`, recordToUpdate)
      .then(() => {
        refreshAfterUpdate();
        if (isUsingTimeBlocks) {
          setSingleDayTimeBlocks(
            createTimeBlocksFromExistingArray(
              recurringSchedule.predetermined_slot_times,
              startTimeValue,
              endTimeValue,
            ),
          );
        }
      })
      .catch((error) => {
        setIsSaving(false);
        notify(error.message, 'error');
      });
  };

  const handleSubmit = async (val) => {
    const values = val;
    setIsSaving(true);
    values.is_demand_based = !isServiceBay;
    if (!id) {
      values.service_vehicle_id = serviceVehicleId;
    }
    if (!values.offered_service_types) {
      values.offered_service_types = offered_service_types;
    }
    if (values.start_time) {
      values.start_time = `${moment(selectedDate).format('YYYY-MM-DD')} ${
        values.start_time
      }:00`;
    }
    if (values.end_time) {
      values.end_time = `${moment(selectedDate).format('YYYY-MM-DD')} ${
        values.end_time
      }:00`;
    }
    if (values.end_time || values.start_time) {
      values.limit_total_hours = getLengthOfDay(
        values.start_time,
        values.end_time,
      );
    }
    if (!allowFutureBookings) {
      values.limit_scheduled_works = 0;
    } else if (Number(values.limit_scheduled_works) === 0) {
      delete values.limit_scheduled_works;
      values.is_canceled = true;
    }
    if (id) {
      update(
        {
          payload: {
            id,
            data: {
              ...values,
              predetermined_slot_times: singleDayTimeBlocks.map(
                (time) => time.time,
              ),
            },
            previousData: { ...formData, offered_service_types: [] },
          },
        },
        {
          onSuccess: () => {
            refreshAfterUpdate();
          },
          onFailure: ({ error }) => {
            setIsSaving(false);
            notify(error.message, 'error');
          },
        },
      );
    } else {
      create(
        {
          payload: {
            data: {
              ...values,
              predetermined_slot_times: singleDayTimeBlocks.map(
                (time) => time.time,
              ),
            },
            previousData: formData,
          },
        },
        {
          onSuccess: () => {
            refreshAfterUpdate();
          },
          onFailure: ({ error }) => {
            setIsSaving(false);
            notify(error.message, 'error');
          },
        },
      );
    }
  };

  const handleTimeChange = (key, value) => {
    if (['start_time', 'end_time'].includes(key)) {
      const timeMinutes = convertToMinutes(value);
      const startTimeMinutes = convertToMinutes(startTimeValue);
      const endTimeMinutes = convertToMinutes(endTimeValue);
      let isInvalid = false;

      if (key.includes('start_time')) {
        setStartTimeValue(value);
        setSingleDayBooleanHasChanged(true);
        if (!isUsingTimeBlocks) return;
        isInvalid = timeMinutes > endTimeMinutes;

        if (!isInvalid) {
          const updatedTimes = singleDayTimeBlocks.map(({ time, duration }) => {
            const currentTimeMinutes = convertToMinutes(time);
            return {
              time,
              duration,
              error:
                currentTimeMinutes < timeMinutes ||
                currentTimeMinutes > endTimeMinutes,
            };
          });
          const recalculatedTimes = recalculateDurations(
            updatedTimes,
            value,
            endTimeValue,
          );
          setSingleDayTimeBlocks(recalculatedTimes);
          setHasSingleDayTimeBlockError(
            recalculatedTimes.some((time) => time.error),
          );
          clearErrors();
        } else {
          setFieldError(isInvalid);
          setStartTimeError(isInvalid);
          setHasSingleDayTimeBlockError(isInvalid);
        }
      } else if (key.includes('end_time')) {
        setEndTimeValue(value);
        setSingleDayBooleanHasChanged(true);
        if (!isUsingTimeBlocks) return;
        isInvalid = timeMinutes < startTimeMinutes;
        setFieldError(isInvalid);
        setEndTimeError(isInvalid);
        if (!isInvalid) {
          const updatedTimes = singleDayTimeBlocks.map(({ time, duration }) => {
            const currentTimeMinutes = convertToMinutes(time);
            return {
              time,
              duration,
              error:
                currentTimeMinutes >= timeMinutes ||
                currentTimeMinutes < startTimeMinutes,
            };
          });
          const recalculatedTimes = recalculateDurations(
            updatedTimes,
            startTimeValue,
            value,
          );
          setSingleDayTimeBlocks(recalculatedTimes);
          setHasSingleDayTimeBlockError(
            recalculatedTimes.some((time) => time.error),
          );
          clearErrors();
        } else {
          setFieldError(isInvalid);
          setEndTimeError(isInvalid);
          setHasSingleDayTimeBlockError(isInvalid);
        }
      }
    }
  };
  const existingBookingText = (multiple = false) => {
    return (
      <Typography>
        <strong>
          There {multiple ? 'are' : 'is'} currently{' '}
          <span
            className={classes.dispatchLink}
            onClick={() =>
              window.open(
                `#/dispatch/${moment(start_time).format('YYYY-MM-DD')}`
              )
            }
          >
            {formData.stats.num_bookings} booking{multiple ? 's' : ''}
          </span>{' '}
          on this day
        </strong>
      </Typography>
    );
  };

  const renderRulesTable = () => (
    <Box className={classes.expandSection}>
      <Box className={classes.compactInfoList}>
        <Typography variant="body2">
          <span className={classes.listLabel}>Appointment Limit: </span>
        </Typography>
        <TextInput
          className={`${classes.listInput} ${classes.width50}`}
          label=""
          source="limit_scheduled_works"
          type="number"
          variant="standard"
          validate={(value) => {
            if (Number(value) === 0) {
              setFieldError(true);
              return 'Limit cannot be 0. Make Day Unavailable instead.';
            }
            if (!value && allowFutureBookings) {
              setFieldError(true);
              return 'Required';
            }
            if (fieldError) {
              setFieldError(false);
            }
            return undefined;
          }}
        />
      </Box>
      {!isServiceBay && (
        <Box className={classes.compactInfoList}>
          <Typography variant="body2">
            <span className={classes.listLabel}>Drive time limit (hr): </span>
          </Typography>
          <TextInput
            className={`${classes.listInput} ${classes.limitInput}`}
            label=""
            source="limit_total_driving_hours"
            type="number"
            variant="standard"
            format={(value) => (value === 0 ? null : value)}
          />
        </Box>
      )}
      {allScheduleTypes.length > 1 && (
        <Box className={classes.compactInfoList}>
          <Typography variant="body2">
            <span className={classes.listLabel}>Service Types:</span>
          </Typography>
          <SelectArrayInput
            variant="standard"
            source="offered_service_types"
            label=""
            choices={allScheduleTypes}
            translateChoice={false}
            onChange={(event) => {
              setServiceTypesIsSet(event.target.value?.length > 0);
            }}
            style={{ maxWidth: '220px' }}
            validate={[required()]}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box style={{ minHeight: formData.id ? '170px' : '100px' }}>
      <Grid
        container
        display="flex"
        style={{ justifyContent: 'space-between' }}
        direction="row"
      >
        <Typography
          item
          variant="subtitle2"
          className={classes.selectedDateText}
        >
          {moment(selectedDate).format('MMMM Do, YYYY')}
        </Typography>
        <Grid item>
          {canResetSchedule && (
            <Button
              color="primary"
              startIcon={<HistoryIcon />}
              size="small"
              className={classes.resetButton}
              onClick={handleResetSchedule}
              disabled={isSaving}
            >
              Revert to Weekly
            </Button>
          )}
        </Grid>
      </Grid>
      <FormWithRedirect
        resource="schedule"
        record={{
          ...formData,
          is_canceled: isCanceled,
          start_time: startTimeValue,
          end_time: endTimeValue,
          offered_service_types: serviceTypes,
          predetermined_slot_times: singleDayTimeBlocks.map(
            (time) => time.time,
          ),
        }}
        save={handleSubmit}
        render={({ handleSubmitWithRedirect, loading, pristine }) => (
          <>
            {!!formData.id && formData?.stats?.num_bookings === 0 && (
              <Box className={classes.infoList}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.vehicleLabel}
                      checked={!allowFutureBookings}
                      color="primary"
                      onChange={() => {
                        setIsCanceled(!isCanceled);
                        if (!allowFutureBookings) setIsFormExpanded(true);
                        setAllowFutureBookings((prev) => !prev);
                        setSingleDayBooleanHasChanged(true);
                      }}
                    />
                  }
                  label="Make Day Unavailable?"
                  source="is_canceled"
                />
              </Box>
            )}
            {formData?.stats?.num_bookings === 1 && existingBookingText()}
            {formData?.stats?.num_bookings > 1 && existingBookingText(true)}
            {!!formData.id && formData?.stats?.num_bookings > 0 && (
              <Box className={classes.infoList}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.vehicleLabel}
                      checked={!allowFutureBookings}
                      color="primary"
                      onChange={() => {
                        if (!allowFutureBookings) setIsFormExpanded(true);
                        setAllowFutureBookings((prev) => !prev);
                        setSingleDayBooleanHasChanged(true);
                      }}
                    />
                  }
                  label="Make Day Unavailable (prevent future customers from booking this day)?"
                />
              </Box>
            )}
            {!formData.id && canResetSchedule && hideTimeInputs && (
              <Box className={classes.infoList}>
                <Typography
                  item
                  variant="subtitle2"
                  className={classes.selectedDateText}
                >
                  Unavailable
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.editButton}
                  onClick={() => setHideTimeInputs(false)}
                >
                  Edit
                </Typography>
              </Box>
            )}
            {!isCanceled && allowFutureBookings && !hideTimeInputs && (
              <>
                <Box className={classes.compactInfoList}>
                  <TextInput
                    className={`${classes.listInput} ${classes.timeInput} ${classes.hideHelperText}`}
                    label=""
                    source="start_time"
                    type="time"
                    variant="outlined"
                    error={startTimeError}
                    onChange={(e) => {
                      handleTimeChange('start_time', e.target.value);
                    }}
                  />
                  <TextInput
                    className={`${classes.listInput} ${classes.timeInput} ${classes.hideHelperText}`}
                    label=""
                    source="end_time"
                    type="time"
                    variant="outlined"
                    error={endTimeError}
                    onChange={(e) => {
                      handleTimeChange('end_time', e.target.value);
                    }}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {isFormExpanded ? (
                      <KeyboardArrowDownIcon
                        className={classes.expandArrow}
                        onClick={() => setIsFormExpanded(false)}
                      />
                    ) : (
                      <KeyboardArrowRightIcon
                        className={classes.expandArrow}
                        onClick={() => setIsFormExpanded(true)}
                      />
                    )}
                    {hasSingleDayTimeBlockError && (
                      <Tooltip
                        title="One of the time blocks has an error. Click here to fix it."
                        aria-label="One of the time blocks has an error. Click here to fix it."
                      >
                        <WarningIcon
                          style={{
                            color: 'red',
                            cursor: 'pointer',
                            marginTop: '6px',
                          }}
                          onClick={() => setIsFormExpanded(true)}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {isFormExpanded &&
                  (isUsingTimeBlocks ? (
                    <AvailabilityFields
                      timeBlocks={singleDayTimeBlocks}
                      setTimeBlocks={setSingleDayTimeBlocks}
                      startTime={startTimeValue}
                      endTime={endTimeValue}
                      renderRulesTable={renderRulesTable}
                    />
                  ) : (
                    renderRulesTable()
                  ))}
              </>
            )}
            {/* Show button for date without a schedule, if they are viewing the full form, if changes have been made, or if the instance has been canceled */}
            {(!formData?.id ||
              isFormExpanded ||
              !pristine ||
              singleDayBooleanHasChanged) &&
              !hideTimeInputs && (
                <Box className={classes.actionButtons} align="center">
                  <SaveButton
                    handleSubmitWithRedirect={handleSubmitWithRedirect}
                    saving={loading || isSaving}
                    disabled={
                      !isCanceled &&
                      (!serviceTypesIsSet ||
                        fieldError ||
                        hasSingleDayTimeBlockError)
                    }
                    style={{ margin: '10px' }}
                    label="Set Single Day"
                  />
                </Box>
              )}
          </>
        )}
      />
    </Box>
  );
};

export default SingleDayAvailabilityForm;
